import type { OsanoConsent } from 'lib/osanoAnalyticsConsent'
import { isOsanoCmpActiveForCurrentUser } from 'lib/osanoCmpScriptSrc'

const OSANO_EVENT_CONSENT_SAVED = 'osano-cm-consent-saved'
const OSANO_EVENT_CONSENT_NEW = 'osano-cm-consent-new'

const REGISTER_RETRY_MS = 250

const REVOCABLE_CATEGORIES = [
  'MARKETING',
  'ANALYTICS',
  'PERSONALIZATION',
] as const

/**
 * True when any non-essential category went from ACCEPT to DENY.
 */
export function didRevokeConsent(
  prev?: OsanoConsent,
  next?: OsanoConsent
): boolean {
  return REVOCABLE_CATEGORIES.some(
    (category) => prev?.[category] === 'ACCEPT' && next?.[category] === 'DENY'
  )
}

/**
 * Calls `onRevoke` when the visitor saves Osano preferences that withdraw a
 * previously granted category. Only explicit saves count (never initialization
 * or per-toggle events), so no reload loops and no reload without Save.
 *
 * `onRevoke` receives the newly saved consent and the prior snapshot so
 * callers can emit analytics (e.g. `osano_consent_saved` only when Analytics
 * went ACCEPT → DENY) before unloading the page.
 *
 * Vendor scripts that already ran cannot be unloaded, so callers should do a
 * full page reload in `onRevoke`.
 */
export function subscribeToOsanoConsentRevocation(
  onRevoke: (consent?: OsanoConsent, previous?: OsanoConsent) => void
): () => void {
  if (!isOsanoCmpActiveForCurrentUser()) {
    return () => undefined
  }

  let unregister: (() => void) | undefined
  let retryTimer: ReturnType<typeof setInterval> | undefined

  const register = (): boolean => {
    const cm = window.Osano?.cm

    if (!cm?.addEventListener || !cm?.removeEventListener) {
      return false
    }

    let previous: OsanoConsent | undefined = cm.getConsent?.()

    const handleSaved = (payload?: OsanoConsent): void => {
      // Prefer Osano's live state; the event payload is only a fallback.
      const next = cm.getConsent?.() ?? payload
      const prior = previous
      const revoked = didRevokeConsent(prior, next)

      previous = next ?? prior

      if (revoked) {
        onRevoke(next, prior)
      }
    }

    cm.addEventListener(OSANO_EVENT_CONSENT_SAVED, handleSaved)
    cm.addEventListener(OSANO_EVENT_CONSENT_NEW, handleSaved)

    unregister = () => {
      cm.removeEventListener?.(OSANO_EVENT_CONSENT_SAVED, handleSaved)
      cm.removeEventListener?.(OSANO_EVENT_CONSENT_NEW, handleSaved)
    }

    return true
  }

  if (!register()) {
    // Osano's event API is not exposed yet; keep retrying until it is, or
    // until the returned cleanup runs.
    retryTimer = setInterval(() => {
      if (register()) {
        clearInterval(retryTimer)
        retryTimer = undefined
      }
    }, REGISTER_RETRY_MS)
  }

  return () => {
    if (retryTimer) {
      clearInterval(retryTimer)
      retryTimer = undefined
    }

    unregister?.()
    unregister = undefined
  }
}
